import { MapPin, Settings, X } from 'lucide-react'
import { useState } from 'react'

interface NearMeControlsProps {
  enabled: boolean
  radiusMiles?: number
  location?: string
  onRadiusChange?: (radius: number) => void
  onDisable?: () => void
}

export function NearMeControls({ enabled, radiusMiles = 5, location, onRadiusChange, onDisable }: NearMeControlsProps) {
  const [showControls, setShowControls] = useState(false)

  if (!enabled) return null

  return (
    <div className="mx-4 md:mx-6 lg:mx-8 mt-2">
      <div className="bg-[var(--stash-blue)]/5 border border-[var(--stash-blue)]/10 rounded-xl px-4 py-3">
        {/* Summary row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[var(--stash-blue)]/10 flex items-center justify-center">
              <MapPin className="w-3.5 h-3.5 text-[var(--stash-blue)]" />
            </div>
            <div>
              <span className="text-xs font-semibold text-[var(--stash-blue)]">
                {radiusMiles.toFixed(1)} mi
              </span>
              {location && (
                <span className="text-xs text-muted-foreground ml-1.5">
                  from {location}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowControls(!showControls)}
              className="p-1.5 rounded-full hover:bg-[var(--stash-blue)]/10 transition-colors"
              aria-label="Near me settings"
            >
              <Settings className="w-3.5 h-3.5 text-[var(--stash-blue)]" />
            </button>
            <button
              onClick={onDisable}
              className="p-1.5 rounded-full hover:bg-destructive/10 transition-colors"
              aria-label="Disable near me"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Expanded controls */}
        {showControls && (
          <div className="mt-3 pt-3 border-t border-[var(--stash-blue)]/10">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-muted-foreground">Search Radius</label>
              <span className="text-sm font-bold text-[var(--stash-blue)]">
                {radiusMiles.toFixed(1)} mi
              </span>
            </div>
            <input
              type="range"
              min="0.5"
              max="50"
              step="0.5"
              value={radiusMiles}
              onChange={(e) => onRadiusChange?.(parseFloat(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--stash-blue)]
                [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer
                bg-[var(--stash-blue)]/20"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>0.5 mi</span>
              <span>50 mi</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
